import * as htmlToImage from "html-to-image";
import { jsPDF } from "jspdf";

type BreakPointRange = { start: number; end: number };

export const handlePrintPdf = (
  id: string,
  fileName: string,
  cb: () => void,
) => {
  const ele = document.getElementById(id);
  if (!ele) return;

  const originalOverflow = window.getComputedStyle(ele).overflow;
  const originalHeight = ele.style.height;
  const originalPosition = ele.style.position;

  ele.style.overflow = "visible";
  ele.style.height = "auto";
  ele.style.position = "relative";

  const getElementBreakPoints = (
    element: HTMLElement,
  ): {
    elementRanges: BreakPointRange[];
  } => {
    const elementRanges: BreakPointRange[] = [];
    const containerRect = element.getBoundingClientRect();

    const importantSelector = ".pdf-component";
    const allElements = new Set<HTMLElement>();

    try {
      element.querySelectorAll(importantSelector).forEach((el) => {
        allElements.add(el as HTMLElement);
      });
    } catch {}

    allElements.forEach((htmlEl) => {
      const rect = htmlEl.getBoundingClientRect();
      const relativeTop = rect.top - containerRect.top;
      const relativeBottom = rect.bottom - containerRect.top;
      if (relativeTop >= 0 && relativeBottom <= containerRect.height) {
        const start = Math.round(relativeTop);
        const end = Math.round(relativeBottom);
        if (end > start) {
          elementRanges.push({ start, end });
        }
      }
    });

    elementRanges.sort((a, b) => a.start - b.start);
    return { elementRanges };
  };

  const { elementRanges: breakPoints } = getElementBreakPoints(ele);

  htmlToImage
    .toCanvas(ele, {
      backgroundColor: "#ffffff",
      pixelRatio: 2,
    })
    .then((canvas) => {
      ele.style.overflow = originalOverflow;
      ele.style.height = originalHeight;
      ele.style.position = originalPosition;

      const imgData = canvas.toDataURL("image/png", 1.0);
      const docs = new jsPDF({
        unit: "px",
        orientation: "portrait",
        format: "a4",
      });
      const pageWidth = docs.internal.pageSize.width;
      const pageHeight = docs.internal.pageSize.height;
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const marginTopFirst = 10;
      const marginTop = 20;
      const marginBottom = 20;
      const contentHeight = pageHeight - marginTop - marginBottom;
      const contentHeightFirst = pageHeight - marginTopFirst - marginBottom;

      if (imgHeight <= contentHeightFirst) {
        docs.addImage(imgData, "PNG", 0, marginTopFirst, imgWidth, imgHeight);
      } else {
        const elementHeight = ele.offsetHeight || ele.scrollHeight;
        const scale = elementHeight > 0 ? canvas.height / elementHeight : 1;

        const elementRangesInCanvas = breakPoints.map((range) => ({
          start: Math.round(range.start * scale),
          end: Math.round(range.end * scale),
        }));

        let sourceY = 0;
        let pageNumber = 0;

        while (sourceY < canvas.height) {
          if (pageNumber > 0) {
            docs.addPage();
          }

          const isFirstPage = pageNumber === 0;
          const currentMarginTop = isFirstPage ? marginTopFirst : marginTop;
          const currentContentHeight = isFirstPage
            ? contentHeightFirst
            : contentHeight;

          const remainingHeight = canvas.height - sourceY;
          const targetHeight =
            Math.round((currentContentHeight / imgHeight) * canvas.height);

          let sourceHeight = Math.min(targetHeight, remainingHeight);

          const pageMaxY = sourceY + sourceHeight;

          const filterRanges = elementRangesInCanvas.filter(
            (range) => range.end - range.start < sourceHeight,
          );
          const inBetweenElement = filterRanges.find(
            (range) => range.start <= pageMaxY && range.end >= pageMaxY,
          );

          if (inBetweenElement) {
            // why +60? to avoid cutting too close to the element boundary
            sourceHeight = inBetweenElement.start - sourceY + 60;
          }

          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          pageCanvas.height = sourceHeight;
          const pageCtx = pageCanvas.getContext("2d");

          if (pageCtx) {
            pageCtx.fillStyle = "#ffffff";
            pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);

            pageCtx.drawImage(
              canvas,
              0,
              sourceY,
              canvas.width,
              sourceHeight,
              0,
              0,
              canvas.width,
              sourceHeight,
            );

            const pageImgHeight = (sourceHeight * imgWidth) / canvas.width;

            const pageImgData = pageCanvas.toDataURL("image/png", 1.0);
            docs.addImage(
              pageImgData,
              "PNG",
              0,
              currentMarginTop,
              imgWidth,
              pageImgHeight,
            );
          }

          sourceY += sourceHeight;
          pageNumber++;
        }
      }

      docs.save(`${fileName}.pdf`);
      cb();
    })
    .catch((error) => {
      ele.style.overflow = originalOverflow;
      ele.style.height = originalHeight;
      ele.style.position = originalPosition;
      console.error("PDF generation error:", error);
      cb();
    });
};
